import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import {
	DefaultLoadingManager,
	FileLoader,
	Group,
	Material,
	Mesh,
	MeshStandardMaterial,
	PCFSoftShadowMap,
	PerspectiveCamera,
	Scene,
	Vector3,
	WebGLRenderer
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import WebRendererSetup from '@/components/Fitstation3DScan/WebRendererSetup';
import ThreeDFeetSetup from '@/components/Fitstation3DScan/ThreeDFeetSetup';
import FootBottomSetup from '@/components/Fitstation3DScan/FootBottomSetup';
import ThreeDFootSetup from '@/components/Fitstation3DScan/ThreeDFootSetup';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import Vue from 'vue';

export default class WebRenderer {
	public OnLoadingStart?: () => void;
	public OnLoadingEnd?: () => void;
	public OnLoaded?: (objects: Group[]) => void;
	public OnUpdate?: () => void;

	public setup?: WebRendererSetup;

	public camera: PerspectiveCamera = new PerspectiveCamera(25, 1, 0.1, 500);
	public scene: Scene = new Scene();
	public renderer: WebGLRenderer = new WebGLRenderer({ alpha: true, antialias: true });
	public controls?: OrbitControls;
	public parentElement?: HTMLElement;
	public objectsOnScene: Group[] = [];

	private readonly elementId: string = '';
	private loaders: ThreeLoaders = new ThreeLoaders();
	private loadedObjs: Group[] = [];
	private inited = false;
	private objsToLoad: number = 0;

	public bufferScene = new Scene();

	constructor() {
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMapType = PCFSoftShadowMap;
	}

	get isLoaded(): boolean {
		return (this.objsToLoad + 1) / (this.loadedObjs.length + 1) == 1;
	}

	public async RenderObjs(objsUrl: string[], isObj: boolean, onLoad?: (group: Group, i: number) => void, onProgress?: (progress: ProgressEvent) => void) {
		this.Init();
		const promises: Promise<Group>[] = [];

		if (this.OnLoadingStart) {
			this.OnLoadingStart();
		}

		const results = [];

		for (let i = 0; i < objsUrl.length; i++) {
			const result = isObj ? await this.loaders.LoadObj(objsUrl[i], onProgress) : await this.loaders.LoadDraco(objsUrl[i], onProgress);
			this.loaders.dracoLoader.dispose();
			// @ts-ignore
			results.push(result);

			this.objsToLoad += 1;
		}

		for (let i = 0; i < results.length; i++) {
			this.ScaleObjToFitCanvas(results[i]);
			this.loadedObjs.push(results[i]);
			if (onLoad) await onLoad(results[i], i);
		}

		if (this.OnLoaded) {
			await this.OnLoaded(this.loadedObjs);
		}

		this.AddLoadedObjToScene();
	}

	private AddLoadedObjToScene() {
		if (!this.isLoaded || this.objsToLoad < 1) {
			return;
		}

		for (let i = 0; i < this.loadedObjs.length; i++) {
			this.bufferScene.add(this.loadedObjs[i]);
			this.objectsOnScene.push(this.loadedObjs[i]);
		}

		this.loadedObjs = [];
		this.objsToLoad = 0;
		if (this.OnLoadingEnd) {
			this.OnLoadingEnd();
		}
	}

	private Init(): void {
		if (this.inited) return;
		if (!this.setup) {
			throw new Error('WEB RENDERER NO SETUP SET');
		}
		this.controls = this.setup?.getControls();
		this.RenderLoop();
		this.inited = true;
		console.log(this);
		// console.log(this.renderer);
		// this.camera.aspect = window.innerWidth / window.innerHeight;
		// this.camera.updateProjectionMatrix();
		// if (window.innerWidth < window.innerHeight) {
		// 	this.renderer.setSize(window.innerWidth * 0.45, window.innerHeight * 0.45);
		// } else {
		// 	this.renderer.setSize(window.innerWidth * 0.8, window.innerHeight * 0.8);
		// }
	}

	public FitToElement(div: HTMLElement) {
		if (!div) {
			throw new Error('[Web renderer] Element of id ' + this.elementId + ' has not been found.');
		}

		console.log('DIVVV', div);

		this.renderer.domElement.style.position = 'absolute';
		this.renderer.domElement.style.top = '50%';
		this.renderer.domElement.style.left = '50%';
		this.renderer.domElement.style.transform = 'translate(-50%, -50%)';
		this.renderer.domElement.style.outline = 'none';
		this.parentElement = div;
		window.addEventListener('resize', this.onResize, false);
		div.appendChild(this.renderer.domElement);
		// const width = div.clientWidth;
		// const height = div.clientHeight;
		// this.camera.aspect = width / height;
		// this.camera.updateProjectionMatrix();
		// this.renderer.setPixelRatio(window.devicePixelRatio);
		// this.renderer.setSize(width, height);

		console.log(window.location.toString().includes('/results'));
		if (window.location.toString().includes('/results')) {
			if (window.innerWidth < window.innerHeight) {
				if (window.innerWidth <= 820) {
					this.camera.aspect = window.innerWidth / window.innerHeight;
					this.camera.updateProjectionMatrix();
					this.renderer.setSize(window.innerWidth * 0.4, window.innerHeight * 0.4);
				} else {
					this.camera.aspect = window.innerWidth / window.innerHeight;
					this.camera.updateProjectionMatrix();
					this.renderer.setSize(window.innerWidth * 0.45, window.innerHeight * 0.45);
				}
			} else {
				if (window.innerHeight <= 820) {
					this.camera.aspect = window.innerWidth / window.innerHeight;
					this.renderer.domElement.style.transform = 'translate(-55%,-50%)';
					this.camera.updateProjectionMatrix();
					this.renderer.setSize(window.innerWidth * 0.7, window.innerHeight * 0.7);
				} else {
					this.camera.aspect = window.innerWidth / window.innerHeight;
					this.camera.updateProjectionMatrix();
					this.renderer.setSize(window.innerWidth * 0.78, window.innerHeight * 0.78);
				}
			}
			// this.camera.aspect = window.innerWidth / window.innerHeight;
			// this.camera.updateProjectionMatrix();
			// this.renderer.setSize(window.innerWidth * 0.8, window.innerHeight * 0.8);
		} else {
			// this.camera.position.x = 0.0;
			// this.camera.position.y = 1.5;
			// this.camera.position.z = 0.18;
			// this.camera.zoom = 1.25;
			this.camera.aspect = div.offsetWidth / div.offsetHeight;
			// console.log('CAMERA', this.camera.position);
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(div.offsetWidth, div.offsetHeight);
		}
		// if (div.offsetWidth < div.offsetHeight) {
		// 	if(window.innerHeight <= 768){
		// 		this.renderer.setSize(div.offsetWidth * 0.7, div.offsetHeight * 0.45);
		// 	}else{
		// 		this.renderer.setSize(div.offsetWidth * 0.45, div.offsetHeight * 0.45);
		// 	}
		// } else {
		// 	this.renderer.setSize(div.offsetWidth, div.offsetHeight);
		// }
	}

	private onResize = () => {
		if (this.parentElement == null) {
			throw new Error('Cannot resize canvas, parent element is null');
		}

		// var tex = new WebGLRenderTarget(this.parentElement.offsetWidth, this.parentElement.offsetHeight, {minFilter: LinearFilter, magFilter: NearestFilter});
		// this.smaterial.uniforms['_PressureMap'] = {value: tex};

		// this.camera.aspect = this.parentElement.clientWidth / this.parentElement.clientHeight;
		// this.camera.zoom = 2;
		// this.camera.updateProjectionMatrix();
		if (window.location.toString().includes('/results')) {
			if (window.innerWidth < window.innerHeight) {
				this.camera.aspect = window.innerWidth / window.innerHeight;
				this.camera.updateProjectionMatrix();
				this.renderer.setSize(window.innerWidth * 0.45, window.innerHeight * 0.45);
			} else {
				this.camera.aspect = window.innerWidth / window.innerHeight;
				this.camera.updateProjectionMatrix();
				this.renderer.setSize(window.innerWidth * 0.8, window.innerHeight * 0.8);
			}
		} else {
			this.camera.aspect = this.parentElement.offsetWidth / this.parentElement.offsetHeight;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(this.parentElement.offsetWidth, this.parentElement.offsetHeight);
		}
		// this.camera.aspect = this.parentElement.offsetWidth / this.parentElement.offsetHeight;
		// this.camera.updateProjectionMatrix();
		// this.renderer.setSize(this.parentElement.offsetWidth * 0.9, this.parentElement.offsetHeight * 0.9);
		// if (window.innerWidth < window.innerHeight) {
		// 	this.renderer.setSize(window.innerWidth * 0.45, window.innerHeight * 0.45);
		// } else {
		// 	this.renderer.setSize(window.innerWidth * 0.8, window.innerHeight * 0.8);
		// }
		// const canvas = this.renderer.domElement;
		// canvas.width = this.parentElement.clientWidth * 2;
		// canvas.height = this.parentElement.clientHeight * 2;
		// canvas.style.width = this.parentElement.clientWidth + 'px';
		// canvas.style.height = this.parentElement.clientHeight + 'px';

		// this.renderer.setSize(this.parentElement.clientWidth, this.parentElement.clientHeight, true);
	};

	public ScaleObjToFitCanvas(obj: Group) {
		obj.traverse((child) => {
			if (child instanceof Mesh) {
				child.geometry.computeBoundingBox();
				let vector3 = new Vector3();
				child.geometry.boundingBox.getSize(vector3);
				let s = 1 / vector3.length();
				obj.scale.set(s, s, s);
				child.geometry.computeBoundingBox();
			}
		});
	}

	private RenderLoop = () => {
		if (this.OnUpdate) {
			this.OnUpdate();
		}

		requestAnimationFrame(this.RenderLoop);
		if (!this.controls) {
			return;
		}
		this.controls.update();
		this.renderer.render(this.bufferScene, this.camera);
	};

	public dispose() {
		for (let obj of this.objectsOnScene) {
			obj.traverse((child) => {
				if (child instanceof Mesh) {
					child.geometry.dispose();
					if (child.material instanceof Material) {
						child.material.dispose();
					} else if (child.material instanceof Array) {
						child.material.forEach((o) => o.dispose());
					}
				}
			});

			//@ts-ignore
			obj = null;
		}

		this.renderer.renderLists.dispose();
		this.bufferScene.dispose();
		this.scene.dispose();
		this.bufferScene = new Scene();
		this.scene = new Scene();
		this.objectsOnScene = [];
	}
}

export class ThreeLoaders {
	public objLoader: OBJLoader = new OBJLoader();
	public fileLoader: FileLoader = new FileLoader(DefaultLoadingManager);
	public dracoLoader: DRACOLoader = new DRACOLoader();

	constructor() {
		this.dracoLoader.setDecoderPath('/draco/');
		this.dracoLoader.setDecoderConfig({ type: 'js' });
	}

	public async LoadObj(url: string, onProgress?: (progress: ProgressEvent) => void): Promise<Group> {
		return new Promise((resolve, reject) => {
			this.objLoader.load(url, resolve, onProgress, reject);
		});
	}

	public async LoadText(url: string) {
		return new Promise((resolve, reject) => {
			this.fileLoader.setResponseType('text');
			this.fileLoader.load(url, resolve, undefined, reject);
		});
	}

	public async LoadDraco(url: string, onProgress?: (progress: ProgressEvent) => void): Promise<Group> {
		this.dracoLoader.setDecoderPath('/draco/');
		this.dracoLoader.setDecoderConfig({ type: 'js' });

		return new Promise((resolve, reject) => {
			this.dracoLoader.load(
				url,
				(geometry) => {
					geometry.computeVertexNormals();
					const material = new MeshStandardMaterial({ color: 0x606060 });
					const mesh = new Mesh(geometry, material);
					mesh.castShadow = true;
					mesh.receiveShadow = true;
					const group = new Group();
					group.add(mesh);
					this.dracoLoader.dispose();
					resolve(group);
					this.dracoLoader.dispose();
					return group;
				},
				onProgress
			);
		});
	}
}
